import { GetOffersResponse } from "@flooron/shared";
import {
  Category,
  PaginatedResponse,
  Product,
  User,
  VerifyOtpPayload,
  VerifyOtpResponse,
} from "./api.response";
import { Order } from "./components/Cards/OrderCard";
import { Address } from "./types";

export const BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.flooron.in";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

class ApiService {
  private refreshSubscribers: Array<(token: string) => void> = [];
  private onSessionExpiredCallback: ((message: string) => void) | null = null;
  private onErrorCallback: ((message: string, type: "error" | "warning") => void) | null = null;
  private onSuccessCallback: ((message: string) => void) | null = null;
  private token: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Load tokens from localStorage on init
    this.token = localStorage.getItem("accessToken");
    this.refreshToken = localStorage.getItem("refreshToken");
  }

  // � Refresh Access Token
  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false;
    try {
      console.log("🔄 Refreshing access token...");

      const response = await fetch(`${BASE_URL}/user/refresh-token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.refreshToken}`,
        },
        credentials: "include", // Include cookies
      });

      if (!response.ok) return false;

      const data = await response.json();
      console.log("✅ Token refreshed successfully");
      // Assume response has new tokens
      if (data.accessToken) {
        this.token = data.accessToken;
        if (data.refreshToken) {
          this.refreshToken = data.refreshToken;
        }
      }
      this.notifyRefreshSubscribers("refreshed");
      return true;
    } catch (error) {
      console.error("❌ Token refresh failed:", error);
      return false;
    }
  }

  // Notify all subscribers about token refresh
  private notifyRefreshSubscribers(token: string): void {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  // Set callback for session expiration
  setSessionExpiredCallback(callback: (message: string) => void): void {
    this.onSessionExpiredCallback = callback;
  }

  // Set callback for error/warning notifications
  setErrorCallback(callback: (message: string, type: "error" | "warning") => void): void {
    this.onErrorCallback = callback;
  }

  // Set callback for success notifications
  setSuccessCallback(callback: (message: string) => void): void {
    this.onSuccessCallback = callback;
  }

  // Show error or warning toast
  private showError(message: string, type: "error" | "warning" = "error"): void {
    if (this.onErrorCallback) {
      this.onErrorCallback(message, type);
    }
  }

  // Show success toast
  private showSuccess(message: string): void {
    if (this.onSuccessCallback) {
      this.onSuccessCallback(message);
    }
  }

  // Set access token
  setTokens(access: string | null, refresh?: string | null): void {
    this.token = access;
    this.refreshToken = refresh || null;
    if (access) {
      localStorage.setItem("accessToken", access);
    } else {
      localStorage.removeItem("accessToken");
    }
    if (refresh) {
      localStorage.setItem("refreshToken", refresh);
    } else {
      localStorage.removeItem("refreshToken");
    }
  }

  // Check if user has valid session
  async hasValidSession(): Promise<boolean> {
    try {
      // Try to fetch user profile - if it succeeds, session is valid
      const headers: Record<string, string> = {};
      if (this.token) {
        headers["Authorization"] = `Bearer ${this.token}`;
      }
      const response = await fetch(`${BASE_URL}/user/profile`, {
        method: "GET",
        credentials: "include",
        headers,
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
  async makeRequest<T>(
    endpoint: string,
    method: HttpMethod = "GET",
    body?: any,
    customHeaders: Record<string, string> = {},
    isRetry: boolean = false,
    suppressErrorToast: boolean = false,
  ): Promise<T> {
    const url = `${BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...customHeaders,
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    // If body is FormData, remove Content-Type header
    if (body instanceof FormData) {
      delete headers["Content-Type"];
    }

    const config: RequestInit = {
      method,
      headers,
      credentials: "include", // Include cookies
    };

    if (body && !(body instanceof FormData)) {
      config.body = JSON.stringify(body);
    } else if (body instanceof FormData) {
      config.body = body;
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 401 && !isRetry && this.refreshToken) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return this.makeRequest<T>(
            endpoint,
            method,
            body,
            customHeaders,
            true,
            suppressErrorToast,
          );
        } else {
          if (this.onSessionExpiredCallback) {
            this.onSessionExpiredCallback(
              "Your session has expired. Please login again.",
            );
          }
          throw new Error("Session expired");
        }
      }

      if (!response.ok) {
        // For DELETE requests with 404, treat as success (item already removed)
        if (method === "DELETE" && response.status === 404) {
          return {} as T;
        }

        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message;
        if (errorMessage && !suppressErrorToast) {
          this.showError(errorMessage, "error");
        }
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      if (response.status === 204) {
        return {} as T;
      }

      const responseData = await response.json();
      
      // Show success message for POST, PUT, DELETE operations (from server response)
      if (method !== "GET" && responseData.message) {
        this.showSuccess(responseData.message);
      }
      
      return responseData;
    } catch (error) {
      const message = error instanceof Error ? error.message : "An error occurred";
      // Only show error if it wasn't already shown from API response and not suppressed
      if (!suppressErrorToast && !message.includes("Session expired") && message !== "HTTP") {
        const isFromAPI = error instanceof Error && error.message !== message;
        if (isFromAPI || !message.startsWith("HTTP")) {
          this.showError(message, "error");
        }
      }
      console.error("API request failed:", error);
      throw error;
    }
  }

  // 📌 User APIs
  getProfile() {
    return this.makeRequest<User>("/user/profile");
  }

  logout() {
    return this.makeRequest("/user/logout", "GET");
  }

  createUser(payload: VerifyOtpPayload) {
    return this.makeRequest<VerifyOtpResponse>(
      "/user/verify-otp",
      "POST",
      payload,
    );
  }

  saveUserDetails(userId: number, formData: FormData) {
    return this.makeRequest<User>(`/user/${userId}`, "PUT", formData);
  }
  updateProfileImage(userId: number, formData: FormData) {
    return this.makeRequest<User>(`/user/${userId}`, "PUT", formData);
  }

  // 📦 Categories & Products
  async getCategories(): Promise<PaginatedResponse<Category>> {
    return await this.makeRequest("/category");
  }

  getProductsByCategory(id: string): Promise<PaginatedResponse<Product>> {
    return this.makeRequest(`/product/category/${id}`);
  }

  getProductsDetail(id: string): Promise<Product> {
    return this.makeRequest(`/product/${id}`);
  }

  // ❤️ Favourites
  addRemoveFavourite(id: string | undefined) {
    if (id === undefined) return;
    return this.makeRequest(`/favorites/${id}`, "POST");
  }

  getFavourites(): Promise<Product> {
    return this.makeRequest("/favorites");
  }

  // 🛒 Cart
  getCart(): Promise<Product> {
    return this.makeRequest("/cart");
  }

  addToCart(productId: string, quantity: number, size?: string | string[]): Promise<JSON> {
    return this.makeRequest("/cart", "POST", { productId, quantity, size });
  }

  removeFromCart(productId: string, size?: string): Promise<JSON> | undefined {
    const url = size ? `/cart/${productId}?size=${encodeURIComponent(size)}` : `/cart/${productId}`;
    return this.makeRequest(url, "DELETE");
  }

  updateCart(productId: string, quantity: number, size?: string) {
    // Suppress error toasts when quantity is 0 (user is removing item)
    const suppressErrorToast = quantity === 0;
    return this.makeRequest("/cart", "PUT", { productId, quantity, size }, {}, false, suppressErrorToast);
  }

  // 🧾 Orders
  getOrders(): Promise<Order[]> {
    return this.makeRequest("/order");
  }

  placeOrder(orderData: any) {
    return this.makeRequest("/order", "POST", orderData);
  }

  // 📍 Addresses
  createAddresses(payload: Address): Promise<Address> {
    return this.makeRequest("/address", "POST", payload);
  }

  getAllAddresses(): Promise<Address[]> {
    return this.makeRequest("/address");
  }

  updateAddress(addressId: string, payload: any) {
    return this.makeRequest(`/address/${addressId}`, "PUT", payload);
  }

  deleteAddress(addressId: string) {
    return this.makeRequest(`/address/${addressId}`, "DELETE");
  }

  getDefaultAddress() {
    return this.makeRequest("/address/default");
  }

  setDefaultAddress(addressId: string) {
    return this.makeRequest(`/address/default/${addressId}`, "PUT");
  }

  getActiveOffers(skip = 0, take = 10): Promise<GetOffersResponse> {
    return this.makeRequest(`/offer/active?skip=${skip}&take=${take}`);
  }

  getOfferById(id: number) {
    return this.makeRequest(`/offer/${id}`);
  }

  getProductsByOffer(offerId: number) {
    return this.makeRequest(`/offer/by-offer/${offerId}`);
  }
  searchProducts(
    query: string,
    skip = 0,
    take = 20,
  ): Promise<PaginatedResponse<Product>> {
    return this.makeRequest<PaginatedResponse<Product>>(
      `/product/search/${query}?skip=${skip}&take=${take}`,
    );
  }
}

const api = new ApiService();
export default api;
